import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faSliders } from "@fortawesome/pro-solid-svg-icons/faSliders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import Popover from "antd/es/popover";
import { useIntl } from "react-intl";

import { CountryRegion } from "@mapmycustomers/shared/enum";
import User from "@mapmycustomers/shared/types/User";
import { Button, ButtonLink, SearchField, SelectField } from "@mapmycustomers/ui";

import UserMultiSelectField from "@app/component/input/UserMultiSelectField";
import { isMember, isReadOnly } from "@app/store/iam/util";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getCountryRegionDisplayName } from "@app/util/ui";

import styles from "./Filter.module.scss";
import messages from "./messages";

const REGIONS = [
  CountryRegion.US_NORTHEAST,
  CountryRegion.US_MIDWEST,
  CountryRegion.US_SOUTH,
  CountryRegion.US_WEST,
  CountryRegion.CA_ATLANTIC,
  CountryRegion.CA_CENTRAL,
  CountryRegion.CA_NORTH,
  CountryRegion.CA_PRAIRIE,
  CountryRegion.CA_WEST_COAST,
];

export interface FilterProps {
  creatorIds: User["id"][];
  onCreatorsChange: (creatorIds: User["id"][]) => void;
  onQueryChange: (query: string) => void;
  onRegionsChange: (regions: CountryRegion[]) => void;
  onSharedWithChange: (sharedWithIds: User["id"][]) => void;
  query: string;
  regions: CountryRegion[];
  sharedWithIds: User["id"][];
}

interface Props extends FilterProps {
  users: User[];
}

const Filter: FC<Props> = ({
  creatorIds,
  onCreatorsChange,
  onQueryChange,
  onRegionsChange,
  onSharedWithChange,
  query,
  regions,
  sharedWithIds,
  users,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const getParentElement = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);

  const handleSearch = useCallback(
    (query: string) => {
      onQueryChange(query.toLowerCase());
      analytics.using(["Search"]);
    },
    [analytics, onQueryChange]
  );

  const handleClearCreators = useCallback(() => onCreatorsChange([]), [onCreatorsChange]);
  const handleClearSharedWith = useCallback(() => onSharedWithChange([]), [onSharedWithChange]);
  const handleClearRegions = useCallback(() => onRegionsChange([]), [onRegionsChange]);

  const regionOptions = useMemo(() => {
    return REGIONS.map((region) => ({
      label: getCountryRegionDisplayName(intl, region),
      value: region,
    }));
  }, [intl]);

  return (
    <div className={styles.container}>
      <SearchField
        allowClear
        className={styles.searchField}
        onChange={handleSearch}
        placeholder={intl.formatMessage(messages.placeholder)}
        size="large"
        value={query}
      />
      <Popover
        content={
          <div className={styles.popoverContent}>
            <div>
              <div className={styles.fieldHeader}>
                <span>{intl.formatMessage(messages.creators)}</span>
                <ButtonLink onClick={handleClearCreators}>
                  {intl.formatMessage(messages.clear)}
                </ButtonLink>
              </div>
              <UserMultiSelectField
                className={styles.selectField}
                onChange={onCreatorsChange}
                placeholder={intl.formatMessage(messages.anyCreator)}
                users={users.filter((user) => !isMember(user) && !isReadOnly(user))}
                value={creatorIds}
              />
            </div>

            <div>
              <div className={styles.fieldHeader}>
                <span>{intl.formatMessage(messages.region)}</span>
                <ButtonLink onClick={handleClearRegions}>
                  {intl.formatMessage(messages.clear)}
                </ButtonLink>
              </div>
              <SelectField<CountryRegion[]>
                mode="multiple"
                onChange={onRegionsChange}
                options={regionOptions}
                placeholder={intl.formatMessage(messages.anyRegion)}
                value={regions}
              />
            </div>

            <div>
              <div className={styles.fieldHeader}>
                <span>{intl.formatMessage(messages.sharedWith)}</span>
                <ButtonLink onClick={handleClearSharedWith}>
                  {intl.formatMessage(messages.clear)}
                </ButtonLink>
              </div>
              <UserMultiSelectField
                className={styles.selectField}
                onChange={onSharedWithChange}
                placeholder={intl.formatMessage(messages.anyMember)}
                users={users}
                value={sharedWithIds}
              />
            </div>
          </div>
        }
        getPopupContainer={getParentElement}
        overlayClassName={styles.popover}
        placement="bottomLeft"
        title={null}
        trigger={["click"]}
      >
        <Button
          icon={
            <Badge
              color={colors.primary100}
              dot={creatorIds.length > 0 || sharedWithIds.length > 0 || regions.length > 0}
            >
              <FontAwesomeIcon icon={faSliders} size="1x" />
            </Badge>
          }
          iconOnly
          size="large"
        />
      </Popover>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps, {})(Filter);
